import { searchUser } from "@/graphql/queries";
import {
  customListCircles,
  customListEvents,
} from "@/graphql/customQueries/index";
import { useQuery } from "@tanstack/react-query";
import { API, graphqlOperation } from "aws-amplify";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

export const useSearchEvents = () => {
  const router = useRouter();
  const [keywords, setKeywords] = useState(router?.query?.q);

  useEffect(() => {
    // Update the keywords when the query changes
    setKeywords(router?.query?.q);
  }, [router.query.q]);
  return useQuery({
    queryKey: ["KEY_SEARCH_EVENT", keywords],
    queryFn: async () => {
      if (!keywords) {
        // Keywords are not available yet, return an empty array or handle as needed
        return [];
      }

      const filterName = transformSearchKeywordToFilterInput(
        keywords as string,
      );
      const response: any = await API.graphql(
        graphqlOperation(customListEvents, {
          filter: {
            or: filterName,
            _deleted: { ne: true },
          },
          limit: 1000,
        }),
      );
      const records: any[] = response?.data?.listEvents?.items ?? [];

      return records;
    },
  });
};

export const useSearchCircles = () => {
  const router = useRouter();
  const [keywords, setKeywords] = useState(router?.query?.q);

  useEffect(() => {
    // Update the keywords when the query changes
    setKeywords(router?.query?.q);
  }, [router.query.q]);

  return useQuery({
    queryKey: ["KEY_SEARCH_CIRCLES", keywords],
    queryFn: async () => {
      if (!keywords) {
        // Keywords are not available yet, return an empty array or handle as needed
        return [];
      }

      const filterName = transformSearchKeywordToFilterInput(
        keywords as string,
      );
      const response: any = await API.graphql(
        graphqlOperation(customListCircles, {
          filter: { or: filterName, _deleted: { ne: true } },
          limit: 1000,
        }),
      );
      const records: any[] = response?.data?.listCircles?.items ?? [];

      return records;
    },
  });
};

type typesWhichExclude = "excludeNonConnection" | "excludeConnection";

export const useSearchProfiles = (whichExclude?: typesWhichExclude) => {
  const router = useRouter();
  const [keyword, keywordSet] = useState<string | undefined>(
    router?.query?.q as string,
  );
  const [connectionType, connectionTypeSet] = useState<
    "SUPER_MENTOR" | "MENTOR" | "MENTEE" | "ALLY" | "FANS"
  >(router?.query?.connection_type as never);
  const [limit, limitSet] = useState<number>(
    parseInt(router?.query?.limit as string),
  );
  const [showChampionUserOnly, showChampionUserOnlySet] = useState<boolean>(
    router?.query?.show_champion_user_only === "true",
  );

  useEffect(() => {
    // Update the keywords when the query changes
    keywordSet(router?.query?.q as string);
    connectionTypeSet(router?.query?.connection_type as never);
    limitSet(router?.query?.limit as never);
    showChampionUserOnlySet(router?.query?.show_champion_user_only as never);
  }, [router.query]);
  return useQuery({
    queryKey: [`KEY_SEARCH_PROFILES`, `${keyword}`],
    queryFn: async () => {
      if (!keyword) {
        // Keywords are not available yet, return an empty array or handle as needed
        return [];
      }

      const inputPayload: {
        keyword?: string;
        connectionType?: string;
        excludeConnection?: boolean;
        excludeNonConnection?: boolean;
        limit?: number;
        showChampionUserOnly?: boolean;
      } = {
        keyword,
        limit,
      };

      if (whichExclude === "excludeConnection") {
        inputPayload.excludeConnection = true;
      }
      if (whichExclude === "excludeNonConnection") {
        inputPayload.excludeNonConnection = true;
      }
      if (connectionType) {
        inputPayload.connectionType = connectionType;
      }
      if (showChampionUserOnly) {
        inputPayload.showChampionUserOnly = showChampionUserOnly;
      }

      const response: any = await API.graphql(
        graphqlOperation(searchUser, {
          input: inputPayload,
        }),
      );
      const records: any[] = response?.data?.searchUser?.data ?? [];

      const global = records.filter((x) => x?.connectionType === null);
      const connections = records.filter((x) => x?.connectionType !== null);

      return { global, connections } as { global: any[]; connections: any[] };
    },
  });
};

interface IFilterConnections {
  connectionType?:
    | "SUPER_MENTOR"
    | "MENTOR"
    | "MENTEE"
    | "ALLY"
    | "FANS"
    | "CIRCLE_OF_CHAMPION"
    | "ALL"
    | "";
  showChampionUserOnly?: boolean;
}

export const useGetAllMyConnections = (filter?: IFilterConnections) => {
  return useQuery({
    queryKey: [
      `KEY_SEARCH_CONNECTIONS_${filter?.connectionType}_${filter?.showChampionUserOnly}`,
      `${filter?.connectionType}_${filter?.showChampionUserOnly}`,
    ],
    queryFn: async () => {
      const response: any = await API.graphql(
        graphqlOperation(searchUser, {
          input: {
            connectionType: filter?.connectionType || null,
            showChampionUserOnly: filter?.showChampionUserOnly,
          },
        }),
      );
      const records: any[] = response?.data?.searchUser?.data ?? [];

      const global = records.filter((x) => x?.connectionType === null);
      const connections = records.filter((x) => x?.connectionType !== null);
      const champion = records.filter((x) => x?.isChampion);

      return { global, connections, champion };
    },
  });
};

const transformSearchKeywordToFilterInput = (keyword: string) => {
  const keywords: string[] = [];
  (keyword as string)
    .trim()
    .split(" ")
    .forEach((k: string) => {
      keywords.push(k);
      keywords.push(k.toUpperCase()); // add upper case keyword
      keywords.push(k.toLowerCase()); // add lower case keyword
      keywords.push(k[0].toUpperCase() + k.slice(1)); // add capital case keyword
    });

  return keywords.map((k) => {
    return { name: { contains: k } };
  });
};
